import React, { useEffect } from 'react';
import connect from 'react-redux/es/connect/connect';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';

import { filterActions } from '../../../behavior/filter/index';
import { dataViewActions } from '../../../behavior/dataview/index';
import { entityType, HISTORY_OVERVIEW_TYPE, recordOptionType } from '../../../constants';
import { Loading } from 'react-lib/dist/components-es';
import PropTypes from 'prop-types';
import FilteringContextProvider from '../../Filtering/FilteringContextProvider';
import { Segment } from 'semantic-ui-react';
import ChemicalProductOverviewForm from './ChemicalProductOverviewForm';
import { initSelectors } from '../../../behavior/init';
import { chemicalProductsActions } from '../../../behavior/chemicalProducts';
import { ChemicalProduct } from '../../../routes';
import { useTitleDispatch } from '../../TitleContextProvider';
import { localStorage } from '../../../store';
import i18n from 'i18next';
import useUser from '@behavior/init/getUserV2';

const ChemicalProductOverview = ({
  actions,
  allFilters,
  entityTypeProps,
  tableOptions,
  showBackendSwitch,
  defaultFilteredList,
  dataViewList,
  id,
  history,
  chemicalProductTitleDetails,
  location,
  t,
  isLoadingDataView,
  latestDataViewId,
  sharedDataView,
  overviewType,
}) => {
  const { user } = useUser();
  const dispatch = useTitleDispatch();
  let defaultFilteredListOverride = JSON.parse(localStorage.getItem('defaultFilteredListOverride'));
  let newSelectedDataViewIdProp =
    location && location.state && location.state.newSelectedDataViewId;

  useEffect(() => {
    if (isLoadingDataView) {
      actions.initializeFiltering(entityTypeProps, latestDataViewId);
    }
  }, [isLoadingDataView]);

  useEffect(() => {
    updateTitle();
  }, [i18n.language, chemicalProductTitleDetails]);

  useEffect(() => {
    //actions.initializeFiltering(entityTypeProps);
    actions.getDataViewList(entityTypeProps);
    actions.getDefaultDataView(entityTypeProps, id, newSelectedDataViewIdProp);

    if (entityTypeProps === entityType.productHist && id)
      actions.getChemicalProductTitleDetails(id);

    return function cleanup() {
      actions.clearDefaultFilteredList();
    };
  }, [newSelectedDataViewIdProp]);

  const filterProps = allFilters[entityTypeProps] || {};

  const updateTitle = () => {
    if (entityTypeProps === entityType.product) {
      dispatch({
        pageTitle: t('page.chemical.product.overview.title'),
      });
    } else if (entityTypeProps === entityType.productHist) {
      if (chemicalProductTitleDetails) {
        dispatch({
          pageTitle: t('page.chemical.product.overview.history.title', {
            title: chemicalProductTitleDetails.tradeName,
          }),
        });
      }
    }
  };

  if (entityTypeProps === entityType.product) {
    if (initSelectors.isInventoryOrChemProductCanCreate(user, entityType.product)) {
      tableOptions = [
        recordOptionType.edit,
        recordOptionType.view,
        recordOptionType.clone,
        recordOptionType.delete,
      ];
    } else {
      tableOptions = [recordOptionType.view];
    }
  } else if (entityTypeProps === entityType.productHist) {
    tableOptions = [recordOptionType.view];
  }

  return !isLoadingDataView &&
    defaultFilteredList !== undefined &&
    entityTypeProps === defaultFilteredList.result.entityType &&
    filterProps.possibleFieldsList !== undefined &&
    filterProps.possibleFieldsList.length > 0 &&
    filterProps.visibleFieldsList.length > 0 &&
    filterProps.possibleColumnsList.length > 0 &&
    filterProps.visibleColumnsList.length > 0 &&
    (JSON.stringify(defaultFilteredList.result.entityType) === JSON.stringify(entityType.product) ||
      JSON.stringify(defaultFilteredList.result.entityType) ===
        JSON.stringify(entityType.productHist)) ? (
    <>
      <FilteringContextProvider
        parentId={id}
        visibleColumns={filterProps.visibleColumnsList}
        visibleFields={filterProps.visibleFieldsList}
        actions={actions}
        entityType={entityTypeProps}
        tableOptions={tableOptions}
        showBackendSwitch={showBackendSwitch}
        filterProps={filterProps}
        defaultFilteredList={defaultFilteredList}
        hazardList={defaultFilteredList.hazardList}
        sharedDataView={sharedDataView}
        selectedRows={{}}
        defaultFilteredListOverride={defaultFilteredListOverride}
      >
        <Segment basic>
          <ChemicalProductOverviewForm
            dataViewList={dataViewList}
            entityTypeProps={entityTypeProps}
            handleAddNewProductClick={() => history.push(`${ChemicalProduct.baseName}/create`)}
            renderRowComparator={overviewType == HISTORY_OVERVIEW_TYPE}
          />
        </Segment>
      </FilteringContextProvider>
    </>
  ) : (
    <Loading isLoading={true} />
  );
};

ChemicalProductOverview.propTypes = {
  actions: PropTypes.shape({
    initializeFiltering: PropTypes.func.isRequired,
    applyFilterAction: PropTypes.func.isRequired,
  }).isRequired,
  allFilters: PropTypes.object.isRequired,
  entityTypeProps: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  tableOptions: PropTypes.array,
  showBackendSwitch: PropTypes.bool,
  hazardList: PropTypes.array,
};

ChemicalProductOverview.defaultProps = {
  tableOptions: [],
  showBackendSwitch: false,
  hazardList: [],
};

const mapStateToProps = (state, ownProps) => ({
  ...state.filter,
  ...state.dataView,
  ...state.chemicalProducts,
  ...state.defaultFilteredList,
  ...state.latestDataViewId, //passed through getDefaultDataView from ViewOptionsContainer > FilteringContextProvider
  allFilters: state.filter,
  id: ownProps.match.params.id,
  entityTypeProps:
    ownProps.match.params.overviewType === HISTORY_OVERVIEW_TYPE
      ? entityType.productHist
      : entityType.product,
  overviewType: ownProps.match.params.overviewType,
  sharedDataView: state.dataView.sharedDataView,
});

const mapDispatchToProps = dispatch => ({
  actions: {
    deleteChemicalProduct: id => dispatch(chemicalProductsActions.deleteChemicalProduct(id, false)),
    initializeFiltering: (entityType, latestDataViewId) =>
      dispatch(filterActions.initializeFiltering(entityType, latestDataViewId)),
    applyFilterAction: (entityType, filterState, filters) =>
      dispatch(filterActions.applyFilterV2(entityType, filterState, filters)),
    clearSharedDataView: () => {
      dispatch(dataViewActions.clearSharedDataView());
    },
    getDefaultDataView: (entityType, id, latestDataViewId) => {
      dispatch(dataViewActions.getDefaultDataView(entityType, id, latestDataViewId));
    },
    getDataViewList: entityType => dispatch(dataViewActions.getDataViewList(entityType)),
    getChemicalProductTitleDetails: id =>
      dispatch(chemicalProductsActions.getChemicalProductTitleDetails(id)),
    clearDefaultFilteredList: () => dispatch(dataViewActions.clearDefaultFilteredList()),
  },
});

export default compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(ChemicalProductOverview);
